import { Button } from '@/components/ui/Button'
import { Icon } from '@/components/ui/Icon'
import { Input } from '@/components/ui/Input'
import { PhoneInput } from '@/components/ui/PhoneInput'
import { useAuthStore } from '@/store/auth'
import { useSnackbarStore } from '@/store/snackbar'
import { ApiError, userService } from '@services'
import { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import styles from './ProfileSettings.module.scss'

interface ProfileFormData {
  name: string
  email: string
  phone: string
}

export const ProfileSettingsPage = () => {
  const navigate = useNavigate()
  const user = useAuthStore((state) => state.user)
  const setAuth = useAuthStore((state) => state.setAuth)
  const authToken = useAuthStore((state) => state.authToken)
  const refreshToken = useAuthStore((state) => state.refreshToken)
  const showSnackbar = useSnackbarStore((state) => state.show)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const methods = useForm<ProfileFormData>({
    defaultValues: {
      name: user?.name || '',
      email: user?.email || '',
      phone: user?.phone || '',
    },
  })

  // Reset form when user data changes
  useEffect(() => {
    if (user) {
      methods.reset({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
      })
    }
  }, [user, methods])

  const onSubmit = async (data: ProfileFormData) => {
    if (!user?.id) {
      showSnackbar('User not found. Please log in again.', 'error')
      return
    }

    setIsSubmitting(true)

    try {
      const updatedUser = await userService.update(user.id, {
        name: data.name,
        email: data.email,
        phone: data.phone,
      })

      // Update the user in the auth store
      if (authToken && refreshToken) {
        setAuth(updatedUser, authToken, refreshToken)
      }

      showSnackbar('Profile updated successfully', 'success')
      navigate('/settings', { replace: true })
    } catch (err) {
      if (err instanceof ApiError && err.data?.message) {
        showSnackbar(err.data.message, 'error')
      } else {
        showSnackbar('Failed to update profile. Please try again.', 'error')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className={styles.container} data-testid="profile-settings-page">
      <div className={styles.content}>
        <header className={styles.header}>
          <Button
            icon={<Icon name="chevron-left" size={20} />}
            onClick={() => navigate('/settings')}
            className={styles.backButton}
            testId="profile-back-button"
          />
          <h1 className={styles.title}>Profile Settings</h1>
        </header>

        <FormProvider {...methods}>
          <form
            onSubmit={methods.handleSubmit(onSubmit)}
            className={styles.form}
            data-testid="profile-form"
          >
            <Input<ProfileFormData>
              name="name"
              type="text"
              label="Name"
              placeholder="Enter your name"
              required
              testId="profile-name-input"
            />

            <Input<ProfileFormData>
              name="email"
              type="email"
              label="Email"
              placeholder="Enter your email"
              required
              testId="profile-email-input"
              validation={{
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: 'Invalid email address',
                },
              }}
            />

            <PhoneInput<ProfileFormData>
              name="phone"
              label="Phone"
              required
              testId="profile-phone-input"
            />

            <Button
              text={isSubmitting ? 'Saving...' : 'Save Changes'}
              variant="primary"
              type="submit"
              disabled={isSubmitting}
              testId="profile-submit-button"
            />
          </form>
        </FormProvider>
      </div>
    </div>
  )
}
